import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface GlassCardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  hoverEffect?: boolean;
  /** Opt out of the hover zoom (scale) only, while keeping the hover bg/shadow. Defaults to true. */
  hoverScale?: boolean;
  style?: React.CSSProperties;
}

// Standard Glass Card
export const GlassCard: React.FC<GlassCardProps> = ({ children, className = '', onClick, hoverEffect = true, hoverScale = true, style }) => {
  return (
    <div 
      onClick={onClick}
      style={style}
      className={`
        relative overflow-hidden
        backdrop-blur-2xl 
        rounded-2xl md:rounded-3xl 
        transition-all duration-500 ease-out
        
        bg-white/40 border border-white/60 
        shadow-[0_8px_30px_rgb(0,0,0,0.04)]
        
        dark:bg-white/[0.03] 
        dark:border-white/[0.08] 
        dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.36)]

        /* Nardo Grey Overrides - Darker background for white text contrast */
        [.nardogrey_&]:bg-zinc-950/30
        [.nardogrey_&]:border-white/10
        [.nardogrey_&]:shadow-[0_8px_32px_0_rgba(0,0,0,0.2)]
        
        ${hoverEffect ? `
          ${hoverScale ? 'hover:scale-[1.01]' : ''} cursor-pointer
          hover:bg-white/60 hover:shadow-xl 
          dark:hover:bg-white/[0.06] dark:hover:shadow-[0_15px_40px_0_rgba(0,0,0,0.5)]
          [.nardogrey_&]:hover:bg-zinc-950/40 [.nardogrey_&]:hover:shadow-[0_15px_40px_0_rgba(0,0,0,0.3)]
        ` : ''}
        ${className}
      `}
    >
      {/* Light Mode Gradient Overlay */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/40 to-transparent pointer-events-none opacity-50 dark:opacity-0 [.nardogrey_&]:opacity-0" />
      
      {/* Dark Mode Gradient Overlay */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/[0.05] to-transparent pointer-events-none opacity-0 dark:opacity-100 [.nardogrey_&]:opacity-0" />
      
      <div className="relative z-10 h-full">
        {children}
      </div>
    </div>
  );
};

// Spotlight Card with Mouse Tracking
export const SpotlightCard: React.FC<GlassCardProps> = ({ children, className = '', onClick, style }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    
    const div = divRef.current;
    const rect = div.getBoundingClientRect();
    
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseEnter = () => setOpacity(1);
  const handleMouseLeave = () => setOpacity(0);

  return (
    <div
      ref={divRef}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={style}
      className={`
        relative overflow-hidden cursor-pointer
        backdrop-blur-2xl rounded-2xl md:rounded-3xl
        border border-white/60 dark:border-white/[0.08] 
        bg-white/40 dark:bg-zinc-900/40 
        
        /* Nardo Grey Overrides */
        [.nardogrey_&]:bg-zinc-950/30
        [.nardogrey_&]:border-white/10

        transition-all duration-300
        hover:scale-[1.01] hover:shadow-2xl
        ${className}
      `}
    >
      <div
        className="pointer-events-none absolute -inset-px opacity-0 transition duration-300"
        style={{
          opacity,
          background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, rgba(255,255,255,0.1), transparent 40%)`,
        }}
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
};

export const AnimatedCounter: React.FC<{ value: string; className?: string }> = ({ value, className }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const numericPart = parseInt(value.replace(/[^0-9]/g, '')) || 0;
  const suffix = value.replace(/[0-9]/g, '');
  const prefix = value.match(/^[^0-9]*/) ? value.match(/^[^0-9]*/)![0] : '';

  useEffect(() => {
    let start = 0;
    const end = numericPart;
    const duration = 2000;
    const incrementTime = 20;
    const steps = duration / incrementTime;
    const increment = end / steps;

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setDisplayValue(end);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, incrementTime);

    return () => clearInterval(timer);
  }, [numericPart]);

  return (
    <span className={className}>
      {prefix}{displayValue}{suffix}
    </span>
  );
};

export const MeshBackground = () => {
  // Creating a high-density, harmonious "Silk" wave effect
  const lineCount = 60;
  const paths = Array.from({ length: lineCount }).map((_, i) => {
    // Percentage 0 to 1
    const t = i / lineCount;
    
    // Vertical spacing that expands slightly towards bottom for perspective
    const yBase = 200 + (i * 12); 
    
    // Wave calculations
    const amplitude = 60 + (i * 2); // Amplitude increases slightly for lower lines
    
    // Control points for a smooth Sine-like Bezier Curve
    // M startX, startY Q cpX, cpY endX, endY
    // We use a multi-segment bezier for more "liquid" feel
    // M 0,y C 400,y-amp 800,y+amp 1600,y
    
    const p1x = 400 + (i * 5); // Shift peak slightly for each line
    const p1y = yBase - amplitude;
    
    const p2x = 900 + (i * 5); // Shift valley
    const p2y = yBase + amplitude;
    
    const p3x = 1600;
    const p3y = yBase;

    const d = `M -200,${yBase} C ${p1x},${p1y} ${p2x},${p2y} ${p3x},${p3y}`;
    
    // Opacity fades at the top and bottom of the stack for smoothness
    const opacity = 0.1 + (Math.sin(t * Math.PI) * 0.3); 
    
    // Generate different durations for sway (vertical) and drift (horizontal)
    // This creates a polyrhythmic animation that never looks repetitive
    const swayDuration = 20 + i * 0.2; 
    const driftDuration = 25 + i * 0.3;

    return {
      d,
      opacity,
      width: 0.5, // Ultra thin high-res lines
      delay: `-${i * 0.5}s`,
      swayDuration: `${swayDuration}s`,
      driftDuration: `${driftDuration}s`
    };
  });

  return (
    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
      {/* Light/Dark Mode Blobs (Hidden in Nardo Grey) */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-500/10 rounded-full blur-[120px] animate-pulse [.nardogrey_&]:hidden" style={{ animationDuration: '8s' }} />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-500/10 rounded-full blur-[120px] animate-pulse [.nardogrey_&]:hidden" style={{ animationDuration: '10s', animationDelay: '1s' }} />
      <div className="absolute top-[40%] left-[40%] w-[30%] h-[30%] bg-cyan-500/10 rounded-full blur-[100px] animate-pulse [.nardogrey_&]:hidden" style={{ animationDuration: '12s', animationDelay: '2s' }} />

      {/* Nardo Grey Mode: High-Fidelity Living Liquid Silk Mesh */}
      <div className="hidden [.nardogrey_&]:block absolute inset-0">
         <svg className="w-full h-full min-w-[1000px]" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="white" stopOpacity="0" />
                <stop offset="20%" stopColor="white" stopOpacity="0.1" />
                <stop offset="50%" stopColor="white" stopOpacity="0.6" />
                <stop offset="80%" stopColor="white" stopOpacity="0.1" />
                <stop offset="100%" stopColor="white" stopOpacity="0" />
              </linearGradient>
            </defs>
            
            {paths.map((p, i) => (
              <path 
                key={i}
                d={p.d}
                fill="none"
                stroke="url(#waveGradient)"
                strokeWidth={p.width}
                style={{ 
                  opacity: p.opacity,
                  // Combine vertical sway and horizontal drift for a living water effect
                  animation: `sway ${p.swayDuration} ease-in-out infinite alternate, drift ${p.driftDuration} ease-in-out infinite alternate`,
                  animationDelay: p.delay
                }}
              />
            ))}
         </svg>
      </div>
    </div>
  );
};

export const SectionTitle: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div className="mb-8 md:mb-12">
    {subtitle && (
      <span className="block mb-2 text-[11px] md:text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400 dark:text-zinc-500 [.nardogrey_&]:text-zinc-300">
        {subtitle}
      </span>
    )}
    <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-zinc-900 dark:text-white/90 [.nardogrey_&]:text-white">
      {title}
    </h2>
  </div>
);

export const GlassButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary';
  /** Internal route target. When set, renders a real react-router <Link> with
      the exact same classes (crawlable href, identical rendering). */
  to?: string;
}> = ({
  children,
  variant = 'primary',
  className = '',
  to,
  ...props
}) => {
  const baseStyles = "px-6 py-3 rounded-full font-medium text-sm md:text-base transition-all duration-300 flex items-center gap-2 justify-center backdrop-blur-xl border active:scale-95";
  
  const variants = {
    primary: `
      bg-zinc-900 text-white border-zinc-800 hover:bg-zinc-800 hover:shadow-lg 
      dark:bg-white/10 dark:border-white/20 dark:text-white dark:hover:bg-white/20 dark:hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]
      [.nardogrey_&]:bg-white [.nardogrey_&]:border-white [.nardogrey_&]:text-zinc-900 [.nardogrey_&]:hover:bg-zinc-200 [.nardogrey_&]:shadow-none
    `,
    secondary: `
      bg-white/30 border-white/40 text-zinc-600 hover:bg-white/50 hover:text-zinc-900 
      dark:bg-transparent dark:border-white/10 dark:text-zinc-400 dark:hover:text-white dark:hover:bg-white/5
      [.nardogrey_&]:bg-white/5 [.nardogrey_&]:border-white/20 [.nardogrey_&]:text-zinc-300 [.nardogrey_&]:hover:text-white [.nardogrey_&]:hover:bg-white/10
    `
  };

  if (to) {
    return (
      <Link to={to} className={`${baseStyles} ${variants[variant]} ${className}`}>
        {children}
      </Link>
    );
  }

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};
